// S3 — Bandeja de Incidencias (P1-P6) + S4 — Resolver Incidencia (modal).
// Spec: docs/spec-mvp/roles/03-supervisor.md §8.
//
//   S3: lista de incidencias del scope con filtros (estado, tipo, severidad,
//       rango) + selección múltiple para "marcar varias como resueltas".
//   S4: modal con detalle completo (contexto de detección, registro
//       relacionado, línea, líder) y acciones resolver / descartar / asignar
//       a otro supervisor de la planta — razón obligatoria.

import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  SeveridadIncidencia,
  SupervisorIncidenciaItem,
  SupervisorIncidenciasQuery,
  TipoIncidencia,
} from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { Tab, Tabs } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SupervisorSidebar } from '../../components/layout/SupervisorSidebar';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import {
  useAsignarIncidencia,
  useDescartarIncidencia,
  useResolverIncidencia,
  useResolverIncidenciasMasivo,
  useSupervisorIncidenciaDetalle,
  useSupervisorIncidencias,
} from '../../lib/api/queries';
import { useCorreccionesParo } from './correccionesParoQueries';
import { S3CorreccionesParoTab } from './S3CorreccionesParoTab';
import { etiquetaLider, severidadLabel } from './etiquetas';

const TIPO_LABEL: Record<TipoIncidencia, string> = {
  P1_sin_captura: 'P1 · Sin captura',
  P2_paro_largo: 'P2 · Paro largo',
  P3_sku_distinto: 'P3 · SKU distinto',
  P4_operarios_inusual: 'P4 · Operarios inusual',
  P5_fuera_turno: 'P5 · Fuera de turno',
  P6_baja_capacidad: 'P6 · Baja capacidad',
  P7_sin_senal_maquina: 'P7 · Sin señal de máquina',
};

const SEV_CLASS: Record<SeveridadIncidencia, string> = {
  critical: 'text-crit',
  warning: 'text-warn',
  info: 'text-fg-tertiary',
};

const fmtDetectada = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(
    d.getHours(),
  ).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const fmtDuracion = (min: number) =>
  min >= 60 ? `${Math.floor(min / 60)}h ${min % 60}m` : `${min}m`;

type EstadoFiltro = 'activas' | 'resuelta' | 'descartada';
type TabS3 = 'incidencias' | 'correcciones';

export function S3BandejaIncidenciasPage() {
  const { state } = useAuth();
  // Pestaña activa por query param (?tab=correcciones) para poder linkear
  // directo desde el dashboard S1 y la campana del sidebar.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: TabS3 = searchParams.get('tab') === 'correcciones' ? 'correcciones' : 'incidencias';
  const setTab = (t: TabS3) => {
    const next = new URLSearchParams(searchParams);
    if (t === 'correcciones') next.set('tab', 'correcciones');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };
  // Conteo de propuestas para el badge de la pestaña (misma query que la
  // pestaña y el sidebar — se cachea).
  const { data: correcciones } = useCorreccionesParo('propuesta');
  const propuestasPendientes = correcciones?.conteos.propuestas ?? 0;
  const [estado, setEstado] = useState<EstadoFiltro>('activas');
  const [tipo, setTipo] = useState('');
  const [severidad, setSeveridad] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [masivoOpen, setMasivoOpen] = useState(false);

  const query: SupervisorIncidenciasQuery = {
    estado,
    tipo: (tipo || undefined) as SupervisorIncidenciasQuery['tipo'],
    severidad: (severidad || undefined) as SupervisorIncidenciasQuery['severidad'],
    desde: desde || undefined,
    hasta: hasta || undefined,
  };
  const { data, isLoading, isError, error, isFetching } = useSupervisorIncidencias(query);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  const items = data?.items ?? [];
  const toggle = (id: string) =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const activasVisibles = items.filter(
    (i) => i.estado === 'pendiente' || i.estado === 'en_revision',
  );
  const todasSeleccionadas =
    activasVisibles.length > 0 && activasVisibles.every((i) => seleccion.has(i.id));

  return (
    <RoleLayout sidebar={<SupervisorSidebar />} roleLabel="Supervisor">
      <div className="flex flex-col gap-4">
        {/* Switch de pestañas: bandeja P1-P6 | correcciones de paro de mtto */}
        <Tabs value={tab} onChange={(v) => setTab(v as TabS3)}>
          <Tab value="incidencias">Incidencias</Tab>
          <Tab value="correcciones">
            <span className="inline-flex items-center gap-2">
              Correcciones de paros
              {propuestasPendientes > 0 ? <Badge tone="warn">{propuestasPendientes}</Badge> : null}
            </span>
          </Tab>
        </Tabs>

        {tab === 'correcciones' ? (
          <S3CorreccionesParoTab />
        ) : (
          <>
            <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
              <div>
                <h1 className="text-xl font-medium text-fg">Bandeja de incidencias</h1>
                <p className="text-sm text-fg-tertiary">
                  Anomalías P1-P6 detectadas por el sistema. Resolvé con acción, descartá con razón
                  o asigne a otro supervisor.
                </p>
              </div>
              {isFetching ? <Spinner className="mb-1 size-4" /> : null}
            </header>

            {/* Filtros */}
            <div className="flex flex-wrap items-end gap-3">
              <SegmentedControl<EstadoFiltro>
                value={estado}
                onChange={(v) => {
                  setEstado(v);
                  setSeleccion(new Set());
                }}
                options={[
                  { value: 'activas', label: `Activas${data ? ` (${data.conteos.activas})` : ''}` },
                  {
                    value: 'resuelta',
                    label: `Resueltas${data ? ` (${data.conteos.resueltas})` : ''}`,
                  },
                  {
                    value: 'descartada',
                    label: `Descartadas${data ? ` (${data.conteos.descartadas})` : ''}`,
                  },
                ]}
              />
              <div className="flex flex-col gap-1">
                <Label htmlFor="s3-tipo">Tipo</Label>
                <Select
                  id="s3-tipo"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  className="w-48"
                >
                  <option value="">Todos</option>
                  {Object.entries(TIPO_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="s3-sev">Severidad</Label>
                <Select
                  id="s3-sev"
                  value={severidad}
                  onChange={(e) => setSeveridad(e.target.value)}
                  className="w-32"
                >
                  <option value="">Todas</option>
                  <option value="critical">{severidadLabel('critical')}</option>
                  <option value="warning">{severidadLabel('warning')}</option>
                  <option value="info">{severidadLabel('info')}</option>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="s3-desde">Desde</Label>
                <Input
                  id="s3-desde"
                  type="date"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                  className="w-38"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="s3-hasta">Hasta</Label>
                <Input
                  id="s3-hasta"
                  type="date"
                  value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                  className="w-38"
                />
              </div>
            </div>

            {/* Barra de acción masiva */}
            {seleccion.size > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-accent/40 bg-accent-soft/40 px-3 py-2 text-sm">
                <span className="text-fg-secondary">
                  <strong className="text-fg">{seleccion.size}</strong> seleccionada
                  {seleccion.size === 1 ? '' : 's'}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Set())}>
                    Limpiar
                  </Button>
                  <Button size="sm" onClick={() => setMasivoOpen(true)}>
                    Resolver seleccionadas
                  </Button>
                </div>
              </div>
            ) : null}

            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-fg-tertiary">
                <Spinner className="size-5" /> Cargando incidencias…
              </div>
            ) : isError ? (
              <p className="py-8 text-center text-sm text-crit">
                {error instanceof Error ? error.message : 'No pudimos cargar las incidencias'}
              </p>
            ) : items.length === 0 ? (
              <div className="rounded border border-dashed border-border p-8 text-center text-sm text-fg-tertiary">
                {estado === 'activas'
                  ? 'Sin incidencias activas en el rango. Al día. ✓'
                  : 'Sin incidencias en el rango con estos filtros.'}
              </div>
            ) : (
              <Card>
                <CardContent className="overflow-auto p-0">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-surface-overlay text-xs text-fg-tertiary">
                      <tr>
                        {estado === 'activas' ? (
                          <th className="w-8 px-3 py-2">
                            <input
                              type="checkbox"
                              aria-label="Seleccionar todas"
                              checked={todasSeleccionadas}
                              onChange={() =>
                                setSeleccion(
                                  todasSeleccionadas
                                    ? new Set()
                                    : new Set(activasVisibles.map((i) => i.id)),
                                )
                              }
                            />
                          </th>
                        ) : null}
                        <th className="px-3 py-2 text-left">Detectada</th>
                        <th className="px-3 py-2 text-left">Sev.</th>
                        <th className="px-3 py-2 text-left">Incidencia</th>
                        <th className="px-3 py-2 text-left">Línea</th>
                        <th className="px-3 py-2 text-left">Planta</th>
                        <th className="px-3 py-2 text-left">Asignada a</th>
                        <th className="px-3 py-2 text-left">
                          {estado === 'activas' ? 'Abierta hace' : 'Cierre'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((i) => (
                        <FilaIncidencia
                          key={i.id}
                          i={i}
                          conCheckbox={estado === 'activas'}
                          checked={seleccion.has(i.id)}
                          onToggle={() => toggle(i.id)}
                          onOpen={() => setDetalleId(i.id)}
                        />
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {detalleId ? (
        <DetalleIncidenciaDialog incidenciaId={detalleId} onClose={() => setDetalleId(null)} />
      ) : null}
      {masivoOpen ? (
        <ResolverMasivoDialog
          ids={[...seleccion]}
          onClose={() => setMasivoOpen(false)}
          onDone={() => {
            setSeleccion(new Set());
            setMasivoOpen(false);
          }}
        />
      ) : null}
    </RoleLayout>
  );
}

function FilaIncidencia({
  i,
  conCheckbox,
  checked,
  onToggle,
  onOpen,
}: {
  i: SupervisorIncidenciaItem;
  conCheckbox: boolean;
  checked: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  return (
    <tr
      className="cursor-pointer border-t border-border/60 transition-colors hover:bg-surface-overlay/40"
      onClick={onOpen}
    >
      {conCheckbox ? (
        <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            aria-label="Seleccionar incidencia"
            checked={checked}
            onChange={onToggle}
          />
        </td>
      ) : null}
      <td className="px-3 py-1.5 whitespace-nowrap tabular-nums text-fg-secondary">
        {fmtDetectada(i.detectada_at)}
      </td>
      <td className={cn('px-3 py-1.5 text-xs font-medium uppercase', SEV_CLASS[i.severidad])}>
        {severidadLabel(i.severidad)}
      </td>
      <td className="max-w-72 truncate px-3 py-1.5" title={i.descripcion}>
        <span className="text-fg-tertiary">{TIPO_LABEL[i.tipo].slice(0, 2)}</span> {i.descripcion}
      </td>
      <td className="px-3 py-1.5 whitespace-nowrap">{i.linea_nombre ?? '—'}</td>
      <td className="px-3 py-1.5 whitespace-nowrap text-fg-secondary">{i.planta_nombre}</td>
      <td className="px-3 py-1.5 whitespace-nowrap text-fg-secondary">
        {i.asignada_a ? `${i.asignada_a.nombre} ${i.asignada_a.apellido}` : '—'}
      </td>
      <td className="px-3 py-1.5 whitespace-nowrap text-fg-secondary">
        {i.estado === 'pendiente' || i.estado === 'en_revision' ? (
          fmtDuracion(i.minutos_abierta)
        ) : (
          <span className={i.estado === 'descartada' ? 'text-fg-tertiary' : 'text-ok'}>
            {i.estado === 'descartada' ? 'descartada' : 'resuelta'}
            {i.resuelta_por ? ` · ${i.resuelta_por.nombre}` : ''}
          </span>
        )}
      </td>
    </tr>
  );
}

// =============================================================================
// S4 — Resolver Incidencia (modal): detalle + resolver / descartar / asignar
// =============================================================================

type AccionS4 = 'resolver' | 'descartar' | 'asignar';

function DetalleIncidenciaDialog({
  incidenciaId,
  onClose,
}: {
  incidenciaId: string;
  onClose: () => void;
}) {
  const { data: d, isLoading, isError, error } = useSupervisorIncidenciaDetalle(incidenciaId);
  const [accion, setAccion] = useState<AccionS4>('resolver');
  const [texto, setTexto] = useState('');
  const [asignadoId, setAsignadoId] = useState('');
  const resolver = useResolverIncidencia();
  const descartar = useDescartarIncidencia();
  const asignar = useAsignarIncidencia();

  const cerrada = d ? d.estado === 'resuelta' || d.estado === 'descartada' : false;
  const pendiente = resolver.isPending || descartar.isPending || asignar.isPending;
  const textoValido = texto.trim().length >= 5;
  const puedeConfirmar = !pendiente && (accion === 'asignar' ? asignadoId !== '' : textoValido);

  const confirmar = async () => {
    try {
      if (accion === 'resolver') {
        await resolver.mutateAsync({ incidenciaId, accion_tomada: texto.trim() });
        toast.success('Incidencia resuelta');
      } else if (accion === 'descartar') {
        await descartar.mutateAsync({ incidenciaId, razon: texto.trim() });
        toast.success('Incidencia descartada');
      } else {
        await asignar.mutateAsync({ incidenciaId, asignada_a_user_id: asignadoId });
        toast.success('Incidencia asignada');
      }
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No pudimos completar la acción');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {d ? `${TIPO_LABEL[d.tipo]} · ${d.linea_nombre ?? d.planta_nombre}` : 'Incidencia'}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-fg-tertiary">
            <Spinner className="size-5" /> Cargando detalle…
          </div>
        ) : isError || !d ? (
          <p className="py-6 text-center text-sm text-crit">
            {error instanceof Error ? error.message : 'No pudimos cargar el detalle'}
          </p>
        ) : (
          <div className="flex flex-col gap-3 text-sm">
            {/* Detalle de la anomalía */}
            <div className="rounded border border-border bg-surface-overlay/40 px-3 py-2">
              <p className="font-medium text-fg">{d.descripcion}</p>
              <p className="mt-1 text-xs text-fg-tertiary">
                <span className={cn('font-medium uppercase', SEV_CLASS[d.severidad])}>
                  {severidadLabel(d.severidad)}
                </span>{' '}
                · detectada {fmtDetectada(d.detectada_at)} · {d.planta_nombre}
                {d.asignada_a
                  ? ` · asignada a ${d.asignada_a.nombre} ${d.asignada_a.apellido}`
                  : ''}
              </p>
            </div>

            {/* Contexto: registro relacionado + líder */}
            {d.registro ? (
              <div className="rounded border border-border px-3 py-2 text-xs text-fg-secondary">
                <p className="mb-1 font-medium uppercase tracking-wide text-fg-tertiary">
                  Registro relacionado
                </p>
                <p>
                  {d.registro.fecha} · bloque {d.registro.hora_inicio_bloque.slice(0, 5)}–
                  {d.registro.hora_fin_bloque.slice(0, 5)} ·{' '}
                  <span className="tabular-nums">{d.registro.sku_codigo_cliente ?? ''}</span>{' '}
                  {d.registro.sku_descripcion ?? ''}
                </p>
                <p className="mt-0.5 tabular-nums">
                  {d.registro.cantidad_procesada.toLocaleString('es-CO')} unidades ·{' '}
                  {d.registro.numero_operarios ?? '—'} operarios ·{' '}
                  {d.registro.validado ? '✓ validado' : 'sin validar'}
                </p>
              </div>
            ) : null}
            {d.lider ? (
              <p className="text-xs text-fg-tertiary">
                {etiquetaLider(d.lider.nombre, d.lider.apellido)}
              </p>
            ) : null}

            {/* Cierre (solo lectura si ya está cerrada) */}
            {cerrada ? (
              <div className="rounded border border-border px-3 py-2 text-xs text-fg-secondary">
                <p className="font-medium text-fg">
                  {d.estado === 'resuelta' ? '✓ Resuelta' : 'Descartada'}
                  {d.resuelta_por ? ` por ${d.resuelta_por.nombre} ${d.resuelta_por.apellido}` : ''}
                </p>
                {d.accion_tomada ? <p className="mt-1">{d.accion_tomada}</p> : null}
              </div>
            ) : (
              <>
                <SegmentedControl<AccionS4>
                  value={accion}
                  onChange={setAccion}
                  options={[
                    { value: 'resolver', label: 'Resolver' },
                    { value: 'descartar', label: 'Descartar' },
                    { value: 'asignar', label: 'Asignar a otro' },
                  ]}
                />
                {accion === 'asignar' ? (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="s4-asignado">Supervisor de la planta</Label>
                    <Select
                      id="s4-asignado"
                      value={asignadoId}
                      onChange={(e) => setAsignadoId(e.target.value)}
                    >
                      <option value="">— Elija —</option>
                      {d.asignables.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.nombre} {a.apellido}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="s4-texto">
                      {accion === 'resolver'
                        ? 'Acción tomada (mín. 5 caracteres)'
                        : 'Razón del descarte (mín. 5 caracteres)'}
                    </Label>
                    <Textarea
                      id="s4-texto"
                      rows={3}
                      value={texto}
                      onChange={(e) => setTexto(e.target.value)}
                      placeholder={
                        accion === 'resolver'
                          ? 'Ej.: se reasignó el líder y se retomó la captura'
                          : 'Ej.: falso positivo — la línea estaba en mantenimiento programado'
                      }
                    />
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={onClose}>
                    Cancelar
                  </Button>
                  <Button
                    variant={accion === 'descartar' ? 'danger' : 'primary'}
                    disabled={!puedeConfirmar}
                    onClick={() => void confirmar()}
                  >
                    {accion === 'resolver'
                      ? 'Resolver'
                      : accion === 'descartar'
                        ? 'Descartar'
                        : 'Asignar'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Acción masiva — "marcar varias como resueltas" con una sola acción/razón
// =============================================================================

function ResolverMasivoDialog({
  ids,
  onClose,
  onDone,
}: {
  ids: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const masivo = useResolverIncidenciasMasivo();
  const [texto, setTexto] = useState('');

  const confirmar = async () => {
    try {
      const r = await masivo.mutateAsync({ ids, accion_tomada: texto.trim() });
      toast.success(
        `${r.resueltas} incidencia${r.resueltas === 1 ? '' : 's'} resuelta${r.resueltas === 1 ? '' : 's'}`,
        r.omitidas > 0 ? { description: `${r.omitidas} omitida(s) — ya cerradas` } : undefined,
      );
      onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No pudimos resolver las incidencias');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Resolver {ids.length} incidencias</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-fg-secondary">
            La misma acción queda registrada (y auditada) en todas las seleccionadas.
          </p>
          <div className="flex flex-col gap-1">
            <Label htmlFor="s3-masivo">Acción tomada (mín. 5 caracteres)</Label>
            <Textarea
              id="s3-masivo"
              rows={3}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Ej.: turno revisado con el líder — capturas normalizadas"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              disabled={texto.trim().length < 5 || masivo.isPending}
              onClick={() => void confirmar()}
            >
              Resolver todas
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
